import { useEffect } from 'react';

import { presencePing } from '../util/api';

const PRESENCE_PING_INTERVAL_MS = 30 * 1000;

/**
 * Global presence pinger.
 *
 * When `enabled` is true (typically tied to `isAuthenticated`), this hook fires a
 * lightweight POST to `/api/presence/ping` every 30 seconds plus once on mount and
 * whenever the tab returns to the foreground. The server bumps
 * `profile.metadata.lastSeenOnline`, which other clients read to decide whether to
 * render the "Online" badge on a listing card (see `ListingCard`).
 *
 * Designed to be invoked from a single, always-mounted component (e.g. the global
 * Topbar) so the schedule is shared across the app and won't double up if multiple
 * pages mount.
 */
export const usePresencePing = (enabled = true) => {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }

    let cancelled = false;
    const fire = () => {
      if (!cancelled) {
        presencePing();
      }
    };

    fire();
    const intervalId = window.setInterval(fire, PRESENCE_PING_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fire();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled]);
};

export default usePresencePing;
